/**
 * Utility functions for processing uploaded files and extracting text content
 */

/**
 * Extract text from a plain text file
 */
async function extractTextFromTxt(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      resolve(text);
    };
    reader.onerror = () => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  });
}

/**
 * Extract text from a PDF file using PDF.js
 */
async function extractTextFromPdf(file: File): Promise<string> {
  try {
    console.log('📄 Starting PDF text extraction for:', file.name);
    
    // Dynamically import pdfjs-dist
    const pdfjsLib = await import('pdfjs-dist');
    console.log('✅ PDF.js library loaded, version:', pdfjsLib.version);
    
    // Set up the worker
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    
    const arrayBuffer = await file.arrayBuffer();
    console.log('✅ File read as ArrayBuffer, size:', arrayBuffer.byteLength);
    
    const pdf = await pdfjsLib.getDocument({ 
      data: arrayBuffer,
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
    }).promise;
    console.log('✅ PDF document loaded, pages:', pdf.numPages);
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
      console.log(`✅ Extracted text from page ${pageNum}, length: ${pageText.length}`);
    }
    
    const trimmedText = fullText.trim();
    console.log('✅ Total extracted text length:', trimmedText.length);
    
    if (trimmedText.length === 0) {
      throw new Error('PDF appears to contain no extractable text. It may be an image-based PDF or scanned document.');
    }
    
    return trimmedText;
  } catch (error) {
    console.error('❌ Error extracting text from PDF:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to extract text from PDF file. Please try converting to text format first.');
  }
}

/**
 * Extract text from a DOCX file using mammoth.js
 */
async function extractTextFromDocx(file: File): Promise<string> {
  try {
    console.log('📄 Starting DOCX text extraction for:', file.name);
    
    // Dynamically import mammoth
    const mammoth = await import('mammoth');
    console.log('✅ Mammoth library loaded');
    
    const arrayBuffer = await file.arrayBuffer();
    console.log('✅ File read as ArrayBuffer, size:', arrayBuffer.byteLength);
    
    const result = await mammoth.extractRawText({ arrayBuffer });
    console.log('✅ Text extracted, length:', result.value.length);
    
    const trimmedText = result.value.trim();
    
    if (trimmedText.length === 0) {
      throw new Error('DOCX file appears to contain no text content.');
    }
    
    return trimmedText;
  } catch (error) {
    console.error('❌ Error extracting text from DOCX:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to extract text from DOCX file. Please try converting to text format first.');
  }
}

/**
 * Process a file and extract its text content based on file type
 */
export async function extractTextFromFile(file: File): Promise<string> {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
  switch (fileExtension) {
    case 'txt':
    case 'text':
    case 'md':
    case 'markdown':
      return await extractTextFromTxt(file);
    
    case 'pdf':
      return await extractTextFromPdf(file);
    
    case 'docx':
      return await extractTextFromDocx(file);
    
    case 'doc':
      throw new Error('Legacy .doc files are not supported. Please save as .docx or .txt format.');
    
    default:
      throw new Error(`Unsupported file type: .${fileExtension}. Please use .txt, .docx, or .pdf files.`);
  }
}

/**
 * Validate file before processing
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const supportedExtensions = ['txt', 'text', 'md', 'markdown', 'pdf', 'docx'];
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
  if (!fileExtension || !supportedExtensions.includes(fileExtension)) {
    return {
      valid: false,
      error: `Unsupported file type. Please use: ${supportedExtensions.join(', ')}`,
    };
  }
  
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size is 10MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    };
  }
  
  return { valid: true };
}

